import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Sticker, Send, Loader2, Ruler, Package, Layers, PenTool, Truck, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SectionHeading } from "./SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATERIALS = [
  { value: "promotional", label: "Promotional (1 Year)" },
  { value: "premium", label: "Premium (5+ Year)" },
  { value: "removable", label: "Removable" },
];

const LAMINATIONS = [
  { value: "none", label: "No Lamination" },
  { value: "gloss", label: "Gloss Lamination" },
  { value: "matte", label: "Matte Lamination" },
];

const ARTWORK = [
  { value: "supply", label: "I will supply artwork" },
  { value: "design", label: "Design my artwork (+$55/design)" },
];

const DELIVERY = [
  { value: "pickup", label: "Pick up - Free" },
  { value: "delivery", label: "Delivery (+$15)" },
];

const fmt = (n) => `$${n.toFixed(2)}`;

export const StickerCalculator = () => {
  const [quantity, setQuantity] = useState(100);
  const [designs, setDesigns] = useState(1);
  const [width, setWidth] = useState(200);
  const [height, setHeight] = useState(200);
  const [material, setMaterial] = useState("promotional");
  const [lamination, setLamination] = useState("none");
  const [artwork, setArtwork] = useState("supply");
  const [delivery, setDelivery] = useState("pickup");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const validDims = width >= 50 && height >= 50 && quantity >= 1 && designs >= 1;

  useEffect(() => {
    if (!validDims) { setQuote(null); return; }
    const t = setTimeout(async () => {
      setQuoting(true);
      try {
        const res = await axios.post(`${API}/quote/stickers`, {
          quantity, designs, width_mm: width, height_mm: height, material, lamination, artwork, delivery,
        });
        setQuote(res.data);
      } catch {
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [quantity, designs, width, height, material, lamination, artwork, delivery]);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email to request this quote.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries/sticker`, {
        name: name.trim(),
        email: email.trim(),
        quote: { quantity, designs, width_mm: width, height_mm: height, material, lamination, artwork, delivery },
      });
      toast.success("Quote request sent — we'll confirm it with you shortly.");
      setName(""); setEmail("");
    } catch {
      toast.error("Couldn't send the request. Please call or email us instead.");
    } finally {
      setSending(false);
    }
  };

  const rows = useMemo(() => quote ? [
    { label: `${quantity} x sticker${quantity > 1 ? "s" : ""}${designs > 1 ? ` (${designs} designs)` : ""}`, value: fmt(quote.subtotal) },
    ...(quote.artwork_fee > 0 ? [{ label: "Artwork / design", value: fmt(quote.artwork_fee) }] : []),
    ...(quote.delivery_fee > 0 ? [{ label: "Delivery", value: fmt(quote.delivery_fee) }] : []),
  ] : [], [quote, quantity, designs]);

  return (
    <section id="stickers" data-testid="sticker-section" className="relative py-28 lg:py-36 bg-navy-light/40 border-y border-white/5">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          testId="sticker-heading"
          eyebrow="Instant price calculator"
          title="Vinyl sticker quote calculator"
          description="Printed and cut vinyl stickers — sized, finished and priced in seconds. Set your specs, then send the quote straight to us and we'll confirm it."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Controls */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="glass rounded-3xl p-8 md:p-10 flex flex-col gap-7"
          >
            <div className="grid grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Package size={15} className="text-signal" />
                  <label className="text-sm text-muted">Quantity</label>
                </div>
                <Input type="number" min={1} max={100000} value={quantity}
                  data-testid="sticker-quantity-input"
                  onChange={(e) => setQuantity(Number(e.target.value) || 0)}
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-signal" />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <LayoutGrid size={15} className="text-signal" />
                  <label className="text-sm text-muted">Number of designs</label>
                </div>
                <Select value={String(designs)} onValueChange={(v) => setDesigns(Number(v))}>
                  <SelectTrigger data-testid="sticker-designs-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)} data-testid={`sticker-designs-${n}`}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <Ruler size={15} className="text-signal" />
                <label className="text-sm text-muted">Sticker size (min 50mm)</label>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-muted/70">Width (mm)</label>
                  <Input type="number" min={50} max={1300} value={width}
                    data-testid="sticker-width-input"
                    onChange={(e) => setWidth(Number(e.target.value) || 0)}
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-signal" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-muted/70">Height (mm)</label>
                  <Input type="number" min={50} max={1300} value={height}
                    data-testid="sticker-height-input"
                    onChange={(e) => setHeight(Number(e.target.value) || 0)}
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-signal" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Layers size={15} className="text-signal" />
                  <label className="text-sm text-muted">Material</label>
                </div>
                <Select value={material} onValueChange={setMaterial}>
                  <SelectTrigger data-testid="sticker-material-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {MATERIALS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`sticker-material-${m.value}`}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Layers size={15} className="text-signal" />
                  <label className="text-sm text-muted">Lamination</label>
                </div>
                <Select value={lamination} onValueChange={setLamination}>
                  <SelectTrigger data-testid="sticker-lamination-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {LAMINATIONS.map((l) => <SelectItem key={l.value} value={l.value} data-testid={`sticker-lamination-${l.value}`}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <PenTool size={15} className="text-signal" />
                  <label className="text-sm text-muted">Artwork</label>
                </div>
                <Select value={artwork} onValueChange={setArtwork}>
                  <SelectTrigger data-testid="sticker-artwork-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {ARTWORK.map((a) => <SelectItem key={a.value} value={a.value} data-testid={`sticker-artwork-${a.value}`}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Truck size={15} className="text-signal" />
                  <label className="text-sm text-muted">Delivery / Pick up</label>
                </div>
                <Select value={delivery} onValueChange={setDelivery}>
                  <SelectTrigger data-testid="sticker-delivery-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {DELIVERY.map((d) => <SelectItem key={d.value} value={d.value} data-testid={`sticker-delivery-${d.value}`}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>

          {/* Quote panel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="glass rounded-3xl p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-signal/10 border border-signal/30 text-signal">
                <Sticker size={20} />
              </span>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Your instant quote</p>
                <p className="font-display text-white font-semibold">
                  {validDims ? `${width} x ${height} mm · ${MATERIALS.find(m => m.value === material).label}` : "Set your sticker specs"}
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 flex-1">
              {quote ? (
                <>
                  {rows.map((r) => (
                    <div key={r.label} className="flex justify-between text-sm">
                      <span className="text-muted">{r.label}</span>
                      <span className="text-white font-medium">{r.value}</span>
                    </div>
                  ))}
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-end justify-between">
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Total (ex GST)</p>
                      <p className="font-display text-4xl font-bold text-white" data-testid="sticker-total">
                        {quoting ? "..." : fmt(quote.total)}
                      </p>
                    </div>
                    <p className="text-muted text-sm text-right" data-testid="sticker-per-sticker">
                      {fmt(quote.per_sticker)} / sticker<br />
                      <span className="text-xs text-muted/70">{fmt(quote.total_inc_gst)} incl. GST</span>
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-muted text-sm">Enter a valid size (min 50mm) and quantity to see your price.</p>
              )}
            </div>

            <form onSubmit={handleRequest} data-testid="sticker-request-form" className="mt-8 pt-6 border-t border-white/10 flex flex-col gap-4">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-signal">// Request this quote</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                  data-testid="sticker-name-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal" required />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                  data-testid="sticker-email-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal" required />
              </div>
              <Button type="submit" disabled={sending || !quote}
                data-testid="sticker-request-button"
                className="rounded-full bg-signal hover:bg-signal-hover text-white font-semibold py-5 neon-glow transition-colors duration-200">
                {sending
                  ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Sending...</span>
                  : <span className="inline-flex items-center gap-2"><Send size={15} /> Send quote request</span>}
              </Button>
              <p className="text-xs text-muted">Indicative pricing in AUD ex GST, based on industry rates plus our margin — we confirm your exact quote before any work starts.</p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
